import { useState, useEffect } from "react";
import { useToast } from "@/shared/hooks/use-toast";

interface UseLutUploadOptions {
  initialLutFilename?: string | null;
  maxSize?: number;
}

export const useLutUpload = ({
  initialLutFilename = null,
  maxSize = 500,
}: UseLutUploadOptions = {}) => {
  const { toast } = useToast();
  const [lutFile, setLutFile] = useState<File | null>(null);
  const [lutFilename, setLutFilename] = useState<string | null>(
    initialLutFilename
  );
  const [lutUrl, setLutUrl] = useState<string | null>(null);
  const [lutError, setLutError] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  // Initialize LUT filename from initial data
  useEffect(() => {
    if (initialLutFilename) {
      console.log(
        "Setting LUT filename from initial data:",
        initialLutFilename
      );
      setLutFilename(initialLutFilename);
    }
  }, [initialLutFilename]);

  // Handle LUT file upload
  const handleLutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSize * 1024) {
      toast({
        title: "Erro ao carregar arquivo LUT",
        description: `O arquivo deve ter no máximo ${maxSize}KB`,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    // Validate file extension (.cube, .3dl, .jpg, .jpeg, etc.)
    const validExtensions = [".cube", ".3dl", ".lut", ".jpg", ".jpeg", ".png"];
    const fileExt = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!validExtensions.includes(fileExt)) {
      toast({
        title: "Formato de arquivo inválido",
        description: "Por favor, envie um arquivo .cube, .3dl, .lut, .jpg, .jpeg ou .png",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    // Clear previous state
    setLutFile(null);
    setLutFilename(null);
    setLutUrl(null);
    
    // Handle image preview using FileReader for base64
    if ([".jpg", ".jpeg", ".png"].includes(fileExt)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLutUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
    
    // Update file and filename
    setLutFile(file);
    setLutFilename(file.name);
    setLutError(false);
  };

  return {
    lutFile,
    lutFilename,
    lutUrl,
    lutError,
    isUploading,
    setLutFile,
    setLutFilename,
    setLutUrl,
    setLutError,
    handleLutChange,
  };
};